import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { db } from "../firebase";
import { useAuth } from "./AuthProvider";
import {
  getDerivedStreakState,
  getRaceStreakAccessState,
  getRaceStreakUiState,
  getWeekKeyForDate,
  isValidTimeZone,
  resolveTimeZone,
} from "../features/streaks/streakUtils";
import { getMembershipAccess } from "../utils/membershipAccess";
import { getLinkedDevice } from "../utils/activityProviders";

const UserContext = createContext(null);
// 60 days * 24 hours * 60 minutes * 60 seconds * 1000 milliseconds
const SOCIAL_FEED_GRACE_PERIOD = 60 * 24 * 60 * 60 * 1000;
// 7 days * 24 hours * 60 minutes * 60 seconds * 1000 milliseconds
const STREAKS_GRACE_PERIOD = 7 * 24 * 60 * 60 * 1000;

function getBrowserTimeZone() {
  try {
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(browserTimeZone) ? browserTimeZone : "";
  } catch (error) {
    return "";
  }
}

function getExpectedStoredMembershipStatus(accessState, hasSubscription) {
  if (accessState === "active" || accessState === "trialing") return "active";
  if (accessState === "canceling_active") return "to_be_cancelled";
  if (accessState === "expired_canceled") return "cancelled";
  if (accessState === "past_due_grace" || accessState === "past_due_expired") {
    return "payment_failed";
  }
  return hasSubscription ? "inactive" : "none";
}

const UserProvider = ({ children }) => {
  const { user } = useAuth();
  const [userDetails, setUserDetails] = useState(null);
  const [userData, setuserData] = useState(null);
  const [userRaces, setUserRaces] = useState(null);
  const [stravaId, setstravaId] = useState(null);
  const [integrationData, setIntegrationData] = useState(undefined);
  const [activeRaces, setActiveRaces] = useState([]);
  const [streakActiveRaces, setStreakActiveRaces] = useState([]);
  const [streakUpcomingRaces, setStreakUpcomingRaces] = useState([]);
  const [streakSummary, setStreakSummary] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [membershipSubscriptions, setMembershipSubscriptions] = useState([]);
  const timezoneSyncAttemptRef = useRef("");
  const membershipSyncAttemptRef = useRef("");
  const browserTimeZone = useMemo(() => getBrowserTimeZone(), []);
  const membershipAccess = useMemo(
    () => getMembershipAccess(membershipSubscriptions, userData?.membershipPaymentGrace || null),
    [membershipSubscriptions, userData?.membershipPaymentGrace]
  );
  const membership = membershipAccess.canAccessMemberContent;
  const hasSocialFeedAccess = userData?.socialFeedAccess === true;
  const linkedDevice = useMemo(() => {
    if (!userData || integrationData === undefined) return null;
    return getLinkedDevice(integrationData, userData);
  }, [integrationData, userData]);

  const FOUNDER_UIDS = [
    // Staging
    "Z6WgrErhbDTvlt2ZWwSN7s7ZgcG3", //diego@bannister.coach
    // Production
    "1s4m6X9CNke1HYG77WgMOE5oODz2",
    "EYAF5TMeNGTUbw0ORJxAf4U7a5a2",
    "fi58WO9zyoV1ZBSTRcbUb6lS1S43",
    "phVhzA888JSk1guUkhYiampWTRt2",
    "AVykOWgsn6gcnhPQz38z4tyMwUI2",
    "93vXwUHtCtRUJen0FiaS3hGUZoP2",
    "qZ3rAcThqyVK8Hm6ZmutPfBzXQY2",
    "8OfvctqUmsVmbZogFBOJZIOSozr2",
    "I2PjsEA90WNiVc7MaXcRuT2KlIL2",
    "pQre7hlHyKTsbYcIKByJTqI7ZkS2",
    "TJwDi1YyYpQSC8OuZFkMx3C1N5y1",
    "w0LRm7YRhYalbz9Sd4930lGIk8L2",
    "NasmS52p5KdS1icNOpU3voMKtW72",
    "sP6lFGejhfYMddzXkeTuanDJpJx1",
    "oe5prvKSvWX8Nafyu40MQMJQZX12",
    "NYw0FaXo68UHnckPCf9EezuCFX23",
    "CIkyJd5r6IhCUVSVTEqiItd7uOD3",
    "1phHiXd4SLSP3PwAG4S16gRrRrM2",
    "LS3HUcT6d1YmeFDe1ajwK9EwGRH3",
    "xgPXqE2OOJUyBsjApCp9QBpkzRR2",
    "DEO16KtPJ8QaFDhIGPKMXmPSrxj1",
    "8rr6eFqMyRbrBIuycLcE8Qg0Ife2",
    "mTu1fOKMMRXywmkexvcQXSc6qLA3",
    "JLtkob9BbNdY6GvCBsvnRgmOiim1",
    "MVnyv17lhud8dn03e4BxQItuke43",
    "xL6fKNJ8e8g2YLxNS2bwhHKY93n2",
    "jtwuUJnugkY4M7EClPO6Q0F1NOt2",
    "n13CCPvti7W4JvUVu59dfrN21ew2",
    "x1dKo4VRTKN97cJp5p2oUpVZIqX2",
    "Hj9r6SiKxhhA8fOLplc20iWunkA3",
    "XzdC4ceBpjbinEv3h6tecvAP3uF2",
    "ZB52JqVd9cheKkubvGnDQOxJL1q2",
    "ttkPIh2vmTTsKNXjLvTTM55VNfa2",
    "ZW0nvIWPUneNLDoyKiw62mrVDAC3",
    "gBX0HCF9EySs9IcPQcdukXLJ7qk2",
    "vIUNx1HhAuQS7LJJb5mbSEeRHOD3",
    "fUZhDndLXbTwKncx55srMzQAjAD3",
    "hKQPXLgg4HeBSI1mXUhaVi4iWrZ2",
    "DvczvCErfhMHIr1KFp61MBuqyTG3",
    "14vT0KCyvWYvKxz7y6YX8qne4qn2",
    "WdkiVU7q6hSrRJVTPWWutyIuZeE2",
    "XKieom1n9nfDMk1GAXx2NI3rXDn2",
    "sysoymzyq8MD5nOQhl6jEF8krhk2",
    "9v0Q1Xf3XwStUMkbEP0f5oq006q1",
    "m5BoGaIxKcWlUwvB4zAJ470Os2t1",
    "1LK4mheNugUqvQQn6E50REocbj92",
    "EHq2jhE342Reyv6DrQMwK2bRz4E2",
    "b1aEEOMSclMRYLCsUnvgKPsFQw42",
    "wQv54ydDWvcnVkvYgm2H8z71cUG2",
    "xWrGLrILoFZD24h9rLBW0TvimcI3",
    "i0UD81Ih3NeEU2uyLDZZN1IznZf1",
    "HzvRzcJUdwXJxr5h14rRgwkQk3G2",
    "OhVCq1WB0rZZmTMx8dnvchjTWKf2",
    "1juyKyxdTBhcOT0cKFG3plsyPuR2",
    "3t6hVdJGuDPTXWFYkH61C3vb2C23",
    "WyO3gdWkHrRhPiKVSr8dDlJzqTv2",
    "1C2s0qivpdfVp3RyAzIWBP17Wkc2",
    "CkPcjww8IfVdxcmL5fmQDYWhDJn1",
    "N0O1zeKsYtV3ekMlevU7eT15C9J2",
    "fGoIVseo0cWXPLqymZtTLv2rXKF3",
    "zSrPCJQcIaa7Nn68WLI9lXzrQl43",
    "HdocEPOVRAUeSYmX6b1aqJaXmQ43",
    "9C1bOTUD9ZeTrqVjrMs4AmCG5ir2",
    "7KflnkDmG1PVy9VD05vbb40sLL02",
    "j7iEOOFNYXfj0XV4QfKTLQnWmT23",
    "TJ0IWJU4jEUtfEkoeh1ysE8A8FY2",
    "KkgHCfzBlGUkypa23sRBc4J1oSn2",
    "qMTacu2TOrUehfRn4GbT6v3NnjZ2",
    "nKY32t7CD3WVhlMUvLWotYljuAm1",
    "6CLNUMHKFldTru2NLIAqyKdzm7n1",
    "13Uuw0rzAhMgjWF1HQ8PoAmpy0s1",
    "qFR1Vli9obcRU9GRrC5S4bZBwAX2",
    "vFPik6OYPfRhc629O46GLiV13Xk2",
    "1xHaLCjAeIQUpvIclCJ23HaAvR93",
    "Lo70kg4eBUZmWhMLVJD33ZEFBf13",
    "NPHwWhFaQdccZ3wsQnxad74ehIf1",
    "2BZMx6gzUSNby60GnMm7aeglXch2",
    "oEEtnZDI14T8splyXW8M7ryReq93",
    "fgxuCCsNtBP1zRcxBsapGLlKia73",
    "LW28gof1TrYnDB49Eaa7bX0UumE3",
    "lDzkc686bAPelIi3124TUOgWvyz2",
    "essJnezVK4N9mfDIZZWcR30kh3u2",
    "a5gT9s9KrnX3Ti3bm8BlXQlXSLh2",
    "7IOuhTAPSyNZS33LFFipiTC599I2",
    "uZsQyUk5IpgyoHvznDeFJwaPCkp2",
    "UjmbiT2uUrV3xXfpP6ZVWoLq3CX2",
    "k6Lwtzd1MQRY8MD1fu3fkiL6m1M2",
    "ud3aH5UKt0OCoWl8zXtaH7Sgblh1",
    "6NgkFSgeolS2YOvfC5qSLugWg5j2",
    "i502fY2PxZa7AeP7euFi3TBXWBB3",
    "9dT2LyWuKdXChmzZKUQevQ5jACk1",
    "EJadTFOuLcOp1eeEUPj9CZAfGJL2",
    "vjcOhvl37MPt4GRFBhDczFXzZBk2",
    "I9eaH8OCK6SfjgbytuWFmTrpYUA2",
    "Cs6RjVAr17NV3UBo717Oo4BkT7s2",
    "l0Bcv6ZmzISwiW7u6BgAB65HvzG3",
    "RvI4khWbqMR8l302vFKb1CbZos73",
    "kapSHnoVtGfJfg91Et4TO1J66wq1",
    "WA3i3kGT9Cb59EgYjhFWjlMltSB3",
    "JkPTcmGUzmhrbB569cKRpeM5XXN2",
    "uZMMZYoDCmYj5sWocY7CclHLWax1",
    "VDtS2Vv7bRfzAYIpGWjMQbBNNK93",
    "HXz1U54pMGeqd0YB7tBRu7G3ryv2",
    "sZFYjICGsDgfVR9dgzgyrljCgTU2",
    "fp6jhdoEjNMxlpazxdpsbbH88KD2",
    "MdZ1uTtIurQZzlE0SLj1eTvUv3E3",
    "CIdQUT46OEcJwofzmaHFjdu910t1",
    "LkJn46ZYqsaI1hE3iaW1LW320q93",
    "P8XmIL5idPa04WjkRv5ycu3bg1w1",
    "T8VP1dYzwngluHAPq50CQhwOeCK2",
    "eTwdqGFp0HbKYOqYLPTr4kRs4J93",
    "tz9GIluvtmMHexEXB0W90D3YwKq1",
    "oEMouOINBUTVUR0iCFI8RtkWbQs1",
    "vew0qOza3YZCQExcApJ8pDzKV8z2",
    "mDbYkl3QbqUvkfhmtf6RpvGD6ok1",
    "Lcg2PO8jC5aSymHcOBo5MIzz01G3",
    "33zSTvwVHhToLxqAN9fSdzvMCFy2",
    "YPgvtTrIpRS11AbwFzjLV81ZSuk1",
    "5UnTI2cgUYh3vPdNsxfRZsi08Pr2",
    "a0p1hghOB0ObXDAtyAxmOST0gs52",
    "8bIgJ0wSigUKj5KEg9I0umVpUzc2",
    "nuNOqOFuLFUNf8cI1D6sXwC9Ff53",
    "RC5DyWkulxbJjA4hxqS8xTYOun73",
    "BAWbgQ4w0wSB3FewZKdo8ZzchYz1",
    "iWoXVDDq85YUY3gaTmSd0PWKlS22",
    "Ifhq2GOWl9hlIZCHp0cSFKHLiIB2",
    "noPSAGPRbrhlMLoyHzE7mX0b3wD2",
    "zuhPHvsjtIUICeTUCsDeF4T6I693",
    "T9v3h2ZnBPhHsQ5sLimiQIv2ZTH3",
    "wn8NvCRNATbHss4hH8AvIzoiH1G2",
    "hCmMyNjCclOop0R7rRXqKSXIiUt2",
    "waxxPTKCRgagnXRErjToQibUrui2",
    "F1WuAor0mEbQqRMwCd1xlbp9As93",
    "i4YYUShzAqcscY55OvYSu9jjRHE3",
    "eDghgvhK2zXaFBWhrmzdLZmpjQx2",
    "Tf715dHqa1Rh2PCwPcUBT8xiQXv2",
    "t2SNZd8H45aOYJvF4hbU1FyZjlh1",
    "8RTAbwwVzVWIBhUWqcir5ZuRA0J2",
    "LaiVxKpIrnegYbeKhHhk1auTQIu1",
    "yiWhI4dS4yVwGJTAjhXtafJbami1",
    "apie8s7sI4UCNe8QE4fAyLa0dwi1",
    "e1DgM7k22lNOuO0teQFVEtfchj72",
    "3BV43y0WgCOvMhMBS4VlHgNokB93",
    "KOzsxdL4yeWJI4wpNkQ0nl079wG3",
    "emyt31odFlQnpNaDO24Sr14swM53",
    "pVJ0ytj7WwUCesBROGAL26P4FkA3",
    "ruoVncxxqZXISQ8rWEPUaXufl2y2",
    "tr21HDQeeIWUT1IeUfsTJYilsAv1",
    "69vQMy56itcz0cmVTpQ0UptCWlz2",
    "QqcfCXNXpPSLBlOtinIY1QFtbMT2",
    "SVinGaVDGgNtGmTXY1zaj85a7OA2",
    "R7kBZjDEkyUnaV4BZrCO1OfhMlx1",
    "mDV3TPktQ5TwZh4l2eLAytzwa233",
    "y3hXXrvCxoWOJ1uVzFgexeQbASs1",
    "FFrr6ftQfvRYYWKSe9npZqwdLaD3",
    "Gf08JTjVdlTQmSpUsUrEXyj6PE52",
    "pRvgeWKnBrZC02JT1GkUjDlD5Fi1",
    "f0K5RByFpkVcGSqX57B4hvntu7q1",
    "ogfe8q2B7UTQazSwXkskdNUZEt73",
    "0b1mazyPcBTk8UlW0djqUhMaMgj1",
    "vMXZqiBBpLSTKnLvgBBumRrMP2t2",
    "DMULnvR9NSVK2mTvwyAo9VCyGR73",
    "exrVsz4hmLZxhDBf0aECqaihvmm2",
    "8ByskR1rm7ZUhDJhHbTSUiaNzwp2",
    "EDHc5rVZ3Fg7uO8HfrPNiQsBkat1",
    "Azjq1JvlzKRc61HOadRq2P7sol03",
    "hUQ2bxcgo7Zk0HveJjPOvmREhep2",
    "huNSp36rFERPfDNKmJKk1zPBLDI3",
    "5qjSR1g5O4ODtxjlbfCtxf5sXhH2",
    "xexwAtd43FZu2US9ZRZ8bDGH0Z32",
    "cRz9MJ2S3cdb9JT26M5o8wLt7oA3",
    "a4r2oNIYJ8bEhkJiuSZoMBN8sHk1",
    "ckDkgPhPMtcRgYy5wNUTADar1sv1",
    "oQEQ2iSx9Ub5kmJjopz4HB1oCdE2",
    "QooXeHqHcYPtkr7xtk8TofVSmwZ2",
    "mGvB56f143QXbDTtHvJkkaGdGo42",
    "FYEj7vbz4TYfJces55KhVtl7VNe2",
    "i6bwISvkWlT7PlnNsc9gc3Im0d63",
    "b4UcIJ5tduXz6Svs5AEpf0gb33f2",
    "WustiRiuaZMqBtZ97h7ulcPmp3j2",
    "0tM7MCkzz6b0HIOXTYTnJpu6Hak2",
    "0tM7MCkzz6b0HIOXTYTnJpu6Hak2",
    "WustiRiuaZMqBtZ97h7ulcPmp3j2",
    "b4UcIJ5tduXz6Svs5AEpf0gb33f2",
    "PckaV8Kwk6ZWoBXc7lpkMGUmjRj2",
    "6gFtwLRbzsVr2DAq7ibqK67v4U03",
    "cwep28gw88RCo9ayMQHrGSbzDDB2",
    "hEbtms7uw5cBYWLUFfF1S2uR0jE3",
  ]

  // const [canSocialFeed, setCanSocialFeed] = useState(false);
  const canSocialFeed = useMemo(() => {
    if (!userData) return false
    return hasSocialFeedAccess || membership === true || activeRaces.length > 0
  }, [userData, hasSocialFeedAccess, membership, activeRaces]);

  const socialFeedEndDate = useMemo(() => {
    if (activeRaces.length <= 0 || hasSocialFeedAccess || membership) return null
    let latestDate = Math.max(...activeRaces.map(r => new Date(r?.raceInfo?.raceDateEnd).getTime()))
    let isInGracePeriod = latestDate < Date.now() && latestDate + SOCIAL_FEED_GRACE_PERIOD > Date.now()
    if (isInGracePeriod) {
      return latestDate + SOCIAL_FEED_GRACE_PERIOD
    } else {
      return null
    }
  }, [hasSocialFeedAccess, membership, activeRaces]);

  const canStreaks = useMemo(() => {
    if (!userData) return false;
    return membership === true || streakActiveRaces.length > 0;
  }, [userData, membership, streakActiveRaces]);

  const canViewStreaks = useMemo(() => {
    if (!userData) return false;
    return membership === true || streakActiveRaces.length > 0 || streakUpcomingRaces.length > 0;
  }, [userData, membership, streakActiveRaces, streakUpcomingRaces]);

  const hasValidRaceAccess = useMemo(() => {
    return activeRaces.length > 0 || streakUpcomingRaces.length > 0;
  }, [activeRaces, streakUpcomingRaces]);

  const canAccessHealthHub = useMemo(() => {
    if (!userData) return false;
    return membership === true || hasValidRaceAccess;
  }, [userData, membership, hasValidRaceAccess]);

  const nextStreakStartDate = useMemo(() => {
    if (membership || streakUpcomingRaces.length <= 0) {
      return null;
    }
    const startDates = streakUpcomingRaces
      .map((race) => new Date(race?.raceInfo?.raceDateStart).getTime())
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => a - b);

    return startDates[0] || null;
  }, [membership, streakUpcomingRaces]);

  const streakEndDate = useMemo(() => {
    if (streakActiveRaces.length <= 0 || membership) return null;
    const latestDate = Math.max(...streakActiveRaces.map((r) => new Date(r?.raceInfo?.raceDateEnd).getTime()));
    const isInGracePeriod = latestDate < Date.now() && latestDate + STREAKS_GRACE_PERIOD > Date.now();
    if (isInGracePeriod) {
      return latestDate + STREAKS_GRACE_PERIOD;
    }
    return null;
  }, [membership, streakActiveRaces]);

  const streakTimeZone = useMemo(
    () => resolveTimeZone(userData?.streakTimeZone || userData?.timeZone),
    [userData?.streakTimeZone, userData?.timeZone]
  );
  const currentStreakWeekKey = useMemo(() => getWeekKeyForDate(new Date(), streakTimeZone), [streakTimeZone]);
  const currentStreakState = useMemo(
    () => getDerivedStreakState(streakSummary, currentStreakWeekKey),
    [currentStreakWeekKey, streakSummary]
  );
  const currentStreakWeeks = useMemo(
    () => currentStreakState.displayedCurrentStreakWeeks,
    [currentStreakState]
  );

  useEffect(() => {
    if (!user?.uid) {
      setStreakSummary(null);
      return undefined;
    }

    return onSnapshot(
      doc(db, "user_activities", user.uid, "summary", "streak"),
      (snap) => {
        setStreakSummary(snap.exists() ? snap.data() : null);
      },
      (error) => {
        console.error("Unable to load streak summary", error);
        setStreakSummary(null);
      }
    );
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setIntegrationData(undefined);
      return undefined;
    }

    setIntegrationData(undefined);
    const unsubscribe = onSnapshot(
      doc(db, "user_integrations", user.uid),
      (snap) => {
        setIntegrationData(snap.exists() ? snap.data() || {} : null);
      },
      (error) => {
        // Connection display must remain compatible with legacy accounts even
        // if the canonical integration document cannot be read.
        console.error("Unable to load activity integration", error);
        setIntegrationData(null);
      }
    );

    return () => {
      unsubscribe();
      setIntegrationData(undefined);
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user) return;
    const onsubUserDetails = onSnapshot(
      doc(db, "users", user.uid),
      async (snap) => {
        // console.log("User doc", snap.exists(),"Details", snap.data()?.details);
        if (snap.exists()) {
          const nextUserData = snap.data() || {};
          setUserDetails({ ...nextUserData.details });
          setuserData(nextUserData);

          const savedStreakTimeZone =
            typeof nextUserData.streakTimeZone === "string" ? nextUserData.streakTimeZone.trim() : "";
          const savedAccountTimeZone =
            typeof nextUserData.timeZone === "string" ? nextUserData.timeZone.trim() : "";
          const timezoneUpdates = {};
          if (!isValidTimeZone(savedAccountTimeZone)) {
            timezoneUpdates.timeZone = browserTimeZone;
          }
          if (!isValidTimeZone(savedStreakTimeZone)) {
            timezoneUpdates.streakTimeZone = browserTimeZone;
          }
          if (
            browserTimeZone &&
            Object.keys(timezoneUpdates).length > 0 &&
            timezoneSyncAttemptRef.current !== browserTimeZone
          ) {
            timezoneSyncAttemptRef.current = browserTimeZone;
            updateDoc(snap.ref, timezoneUpdates).catch((error) => {
              timezoneSyncAttemptRef.current = "";
              console.error("Unable to save timezone to user profile", error);
            });
          }

          let racesMap = new Map();
          const subRaces = nextUserData.subscribedRaces;
          if (subRaces) {
            for (const [key, value] of Object.entries(subRaces)) {
              racesMap.set(key, value);
            }
          }

          const newActiveRaces = Array.from(racesMap.values()).filter((r) => {
            if (!r?.raceInfo?.raceDateEnd || !r?.raceInfo?.raceDateStart)
              return false;
            // const dStart = new Date(r?.raceInfo?.raceDateStart);
            const dEnd = new Date(r?.raceInfo?.raceDateEnd);
            return dEnd.getTime() + SOCIAL_FEED_GRACE_PERIOD > Date.now();
          });
          const newStreakActiveRaces = Array.from(racesMap.values()).filter((r) => {
            if (!r?.raceInfo?.raceDateEnd || !r?.raceInfo?.raceDateStart) {
              return false;
            }
            const accessState = getRaceStreakAccessState(r, Date.now());
            return accessState === "active" || accessState === "grace";
          });
          const newStreakUpcomingRaces = Array.from(racesMap.values()).filter((r) => {
            if (!r?.raceInfo?.raceDateEnd || !r?.raceInfo?.raceDateStart) {
              return false;
            }
            return getRaceStreakUiState(r, Date.now()) === "upcoming";
          });
          setActiveRaces(newActiveRaces);
          setStreakActiveRaces(newStreakActiveRaces);
          setStreakUpcomingRaces(newStreakUpcomingRaces);
          setUserRaces(racesMap);
          setstravaId(nextUserData.stravaAccount?.id || null);
        }
        setUserLoading(false);
      },
    );
    // async function getUserDetails() {
    //   const savedDetails = await getDoc(doc(db, "users", user.uid));
    // }
    // getUserDetails();
    return () => {
      timezoneSyncAttemptRef.current = "";
      setUserDetails(null);
      setuserData(null)
      // setCanSocialFeed(false)
      setUserRaces(new Map());
      setActiveRaces([]);
      setStreakActiveRaces([]);
      setStreakUpcomingRaces([]);
      setStreakSummary(null);
      onsubUserDetails();
    };
  }, [browserTimeZone, user]);

  useEffect(() => {
    if (!user?.uid) {
      setMembershipSubscriptions([]);
      membershipSyncAttemptRef.current = "";
      return undefined;
    }

    return onSnapshot(
      collection(db, "stripe_customers", user.uid, "subscriptions"),
      (snap) => {
        const subscriptions = snap.docs.map((subscriptionDoc) => ({
          ...subscriptionDoc.data(),
          id: subscriptionDoc.id,
        }));
        setMembershipSubscriptions(subscriptions);
      },
      (error) => {
        console.error("Unable to load membership subscriptions", error);
        setMembershipSubscriptions([]);
      }
    );
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      membershipSyncAttemptRef.current = "";
      return;
    }

    const subscription = membershipAccess.subscription;
    if (!subscription?.id) return;

    const stripeStatus = typeof subscription.status === "string"
      ? subscription.status.toLowerCase()
      : null;
    const expectedStatus = getExpectedStoredMembershipStatus(
      membershipAccess.state,
      true
    );
    const storedStatus = userData?.membershipStatus;
    const needsGraceSync = stripeStatus === "past_due" &&
      userData?.membershipPaymentGrace?.subscriptionId !== subscription.id;
    const needsStatusSync = !storedStatus ||
      storedStatus.subscriptionId !== subscription.id ||
      storedStatus.stripeStatus !== stripeStatus ||
      storedStatus.status !== expectedStatus;
    if (!needsGraceSync && !needsStatusSync) return;

    const syncKey = `${subscription.id}:${stripeStatus}:${expectedStatus}`;
    if (membershipSyncAttemptRef.current === syncKey) return;

    membershipSyncAttemptRef.current = syncKey;
    const syncMembershipAccess = httpsCallable(getFunctions(), "syncMembershipAccess");
    syncMembershipAccess().catch((error) => {
      membershipSyncAttemptRef.current = "";
      console.error("Unable to refresh membership access", error);
    });
  }, [
    membershipAccess.state,
    membershipAccess.subscription,
    user?.uid,
    userData?.membershipPaymentGrace?.subscriptionId,
    userData?.membershipStatus,
  ]);

  return (
    <UserContext.Provider
      value={{
        user,
        userDetails,
        userRaces,
        userData,
        stravaId,
        linkedDevice,
        canSocialFeed,
        socialFeedEndDate,
        canStreaks,
        canViewStreaks,
        canAccessHealthHub,
        membershipAccess,
        hasValidRaceAccess,
        streakEndDate,
        currentStreakWeeks,
        streakUpcomingRaces,
        hasUpcomingStreakRace: streakUpcomingRaces.length > 0,
        nextStreakStartDate,
        activeRaces,
        userLoading,
        membership,
        FOUNDER_UIDS
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

function useUserContext() {
  return useContext(UserContext);
}

export default UserProvider;
export { useUserContext };
